/**
 * Juice.ts — screen shake, squash/stretch, glow pulses, confetti + haptics.
 *
 * Calm Premium rule: baseline interactions stay gentle. Intensity scales ONLY
 * with win size — small wins barely nudge, legendary wins shake the room.
 */
import { Container, Graphics } from 'pixi.js';
import { Tweens } from '../util/tween';
import { easeOutCubic, easeOutQuart } from '../util/ease';
import { Rng } from '../util/rng';
import { reducedMotion } from '../util/motion';
import theme from '../render/theme';

const rng = new Rng('juice-' + Math.random());

export class Juice {
  /** the container to shake (usually the world root). */
  private shakeTarget: Container | null = null;
  private shakeBaseX = 0;
  private shakeBaseY = 0;
  private shakeAmt = 0;

  attachShakeTarget(c: Container): void {
    this.shakeTarget = c;
    this.shakeBaseX = c.position.x;
    this.shakeBaseY = c.position.y;
  }

  /** trigger a shake; intensity 0..1. */
  shake(intensity: number): void {
    if (!this.shakeTarget || reducedMotion()) return; // no screen-shake when motion is reduced
    this.shakeAmt = Math.max(this.shakeAmt, 4 + intensity * 22);
    Tweens.to({
      from: this.shakeAmt,
      to: 0,
      duration: 380 + intensity * 420,
      ease: easeOutQuart,
      onUpdate: (v) => (this.shakeAmt = v),
    });
  }

  /** call each frame to apply shake offset. */
  update(): void {
    if (!this.shakeTarget) return;
    if (this.shakeAmt > 0.05) {
      this.shakeTarget.position.set(
        this.shakeBaseX + (rng.float() - 0.5) * this.shakeAmt,
        this.shakeBaseY + (rng.float() - 0.5) * this.shakeAmt,
      );
    } else {
      this.shakeTarget.position.set(this.shakeBaseX, this.shakeBaseY);
    }
  }

  /** squash/stretch a node briefly. amount ~0.1-0.3. */
  static squash(node: Container, amount = 0.16, dur: number = theme.motion.base): void {
    const baseX = node.scale.x;
    const baseY = node.scale.y;
    Tweens.to({
      from: 0,
      to: 1,
      duration: dur,
      ease: easeOutCubic,
      onUpdate: (t) => {
        const k = Math.sin(t * Math.PI);
        node.scale.set(baseX * (1 + amount * k), baseY * (1 - amount * k));
      },
      onComplete: () => node.scale.set(baseX, baseY),
    });
  }

  /** A soft expanding glow ring at (x,y). */
  static glowPing(parent: Container, x: number, y: number, color: number, size = 60): void {
    const g = new Graphics();
    g.circle(0, 0, size).fill({ color, alpha: 0.0 });
    g.position.set(x, y);
    parent.addChild(g);
    Tweens.to({
      from: 0,
      to: 1,
      duration: 520,
      ease: easeOutQuart,
      onUpdate: (t) => {
        g.clear();
        const r = size * (0.4 + t * 1.4);
        g.circle(0, 0, r).stroke({ width: 3 * (1 - t) + 0.5, color, alpha: 0.5 * (1 - t) });
        g.circle(0, 0, r * 0.7).fill({ color, alpha: 0.12 * (1 - t) });
      },
      onComplete: () => g.destroy(),
    });
  }

  /** Trigger device haptics if supported, scaled to intensity. */
  static haptic(intensity: number): void {
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    try {
      if (intensity >= 0.85) navigator.vibrate([18, 30, 18, 30, 40]);
      else if (intensity >= 0.5) navigator.vibrate([12, 24, 18]);
      else if (intensity >= 0.2) navigator.vibrate(10);
      else navigator.vibrate(5);
    } catch {
      /* ignore */
    }
  }
}

/** A burst of particles (stardust / coins / confetti). */
export class ParticleBurst {
  static stardust(
    parent: Container,
    x: number,
    y: number,
    count: number,
    colors: number[],
    spread = 110,
  ): void {
    if (reducedMotion()) count = Math.min(count, 8);
    for (let i = 0; i < count; i++) {
      const p = new Graphics();
      const col = colors[i % colors.length];
      const r = 2 + rng.float() * 4;
      const star = rng.float() < 0.4;
      if (star) drawStar(p, r * 2, col);
      else p.circle(0, 0, r).fill({ color: col });
      p.position.set(x, y);
      parent.addChild(p);
      const ang = rng.range(0, Math.PI * 2);
      const dist = spread * (0.4 + rng.float() * 0.9);
      const tx = x + Math.cos(ang) * dist;
      const ty = y + Math.sin(ang) * dist - spread * 0.3;
      const dur = 600 + rng.float() * 500;
      const rot = rng.range(-3, 3);
      Tweens.to({
        from: 0,
        to: 1,
        duration: dur,
        ease: easeOutQuart,
        onUpdate: (t) => {
          p.position.set(x + (tx - x) * t, y + (ty - y) * t + 40 * t * t);
          p.alpha = 1 - t * t;
          p.rotation = rot * t;
          p.scale.set(1 - t * 0.4);
        },
        onComplete: () => p.destroy(),
      });
    }
  }

  static confetti(
    parent: Container,
    width: number,
    count: number,
    colors: number[],
  ): void {
    if (reducedMotion()) count = Math.min(count, 10);
    for (let i = 0; i < count; i++) {
      const p = new Graphics();
      const col = colors[i % colors.length];
      const w = 6 + rng.float() * 8;
      const h = 10 + rng.float() * 10;
      p.rect(-w / 2, -h / 2, w, h).fill({ color: col });
      const x = rng.float() * width;
      const y = -30 - rng.float() * 200;
      p.position.set(x, y);
      p.rotation = rng.range(0, Math.PI);
      parent.addChild(p);
      const fall = 800 + rng.float() * 500;
      const dur = 1600 + rng.float() * 1400;
      const sway = rng.range(-60, 60);
      const spin = rng.range(-8, 8);
      Tweens.to({
        from: 0,
        to: 1,
        duration: dur,
        ease: easeOutCubic,
        onUpdate: (t) => {
          p.position.set(x + Math.sin(t * 6) * sway, y + fall * t);
          p.rotation += spin * 0.02;
          p.alpha = t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1;
        },
        onComplete: () => p.destroy(),
      });
    }
  }
}

function drawStar(g: Graphics, r: number, color: number): void {
  const spikes = 4;
  const outer = r;
  const inner = r * 0.42;
  for (let i = 0; i < spikes * 2; i++) {
    const rad = i % 2 === 0 ? outer : inner;
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(a) * rad;
    const y = Math.sin(a) * rad;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.fill({ color });
}
